import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import {
  Webhook, Plug, Plus, Trash2, Send, RefreshCw, X, Check, Copy,
  Radio, Zap, ShieldCheck, KeyRound, Power, PowerOff, Activity,
  UserPlus, Megaphone, ShoppingCart, CreditCard, Inbox, MessageSquare, Link2,
} from 'lucide-react'
import { getSession } from '../lib/session'

// Integrations — the developer console for outbound webhooks + the event stream.
// Tenants register endpoints (URL + subscribed event types + a signing secret);
// other modules emit events, each of which logs here and enqueues one signed
// delivery per subscribed endpoint. This page is the register/inspect surface.
//
// VISUAL LAYER: the flat "Nunito / Intelligence Briefings" aesthetic, scoped under
// `.ig-root` so nothing leaks. Same tokens as GrowthLab / Marketing Brain.
const API = '/api/v1/integrations'

// ---- scoped stylesheet (dashboard tokens, prefixed) -----------------------
const IG_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.ig-root{
  --ig-bg:#FBFBF7; --ig-panel:#FFFFFF; --ig-panel2:#F6F6F0; --ig-line:#ECEBE3;
  --ig-ink:#2E3A45; --ig-mut:#7C8794; --ig-dim:#AEB6BD;
  --ig-accent:#4E9AC4; --ig-up:#4FAE5A; --ig-down:#E24A3B; --ig-warn:#EAB43C; --ig-purple:#8E7CC3;
  --ig-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--ig-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .ig-root{
  --ig-bg:#0E1116; --ig-panel:#171B22; --ig-panel2:#12161C; --ig-line:#262C36;
  --ig-ink:#E7ECF1; --ig-mut:#95A0AD; --ig-dim:#5C6672;
  --ig-accent:#5FB0DB; --ig-up:#5CC47C; --ig-down:#F0575C; --ig-warn:#EAB43C; --ig-purple:#A796D6;
  --ig-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .ig-root{
  --ig-bg:#0E1116; --ig-panel:#171B22; --ig-panel2:#12161C; --ig-line:#262C36;
  --ig-ink:#E7ECF1; --ig-mut:#95A0AD; --ig-dim:#5C6672;
  --ig-accent:#5FB0DB; --ig-up:#5CC47C; --ig-down:#F0575C; --ig-warn:#EAB43C; --ig-purple:#A796D6;
  --ig-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}}
.ig-root *{box-sizing:border-box;}
.ig-title{font-size:24px;font-weight:800;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--ig-ink);}
.ig-sub{margin:4px 0 0;color:var(--ig-mut);font-size:14px;font-weight:500;line-height:1.5;}
.ig-card{background:var(--ig-panel);border:1px solid var(--ig-line);border-radius:20px;box-shadow:var(--ig-shadow);padding:20px 22px;}
.ig-card.tight{padding:16px 18px;}
.ig-soft{background:var(--ig-panel2);border:1px solid var(--ig-line);border-radius:14px;}
.ig-sec-h{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--ig-mut);display:flex;align-items:center;gap:8px;margin:0;}
.ig-elab{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--ig-mut);margin:0;display:flex;align-items:center;gap:6px;}
.ig-narr{font-size:15px;font-weight:700;line-height:1.5;color:var(--ig-ink);margin:0;}
.ig-body{font-size:14px;font-weight:600;line-height:1.55;color:var(--ig-ink);margin:0;}
.ig-muted{color:var(--ig-mut);font-weight:600;}
.ig-dim{color:var(--ig-dim);font-weight:600;}
.ig-tabnum{font-variant-numeric:tabular-nums;}
.ig-mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;}
.ig-pill{font-size:11px;font-weight:800;padding:4px 11px;border-radius:20px;display:inline-flex;align-items:center;gap:5px;text-transform:capitalize;}
.ig-chip{font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;background:var(--ig-panel2);border:1px solid var(--ig-line);color:var(--ig-mut);display:inline-flex;align-items:center;gap:5px;white-space:nowrap;}
.ig-icobox{flex:none;width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;}
.ig-btn{font-family:Nunito,system-ui,sans-serif;font-size:13px;font-weight:800;border-radius:12px;padding:9px 15px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;border:1px solid var(--ig-line);background:var(--ig-panel);color:var(--ig-ink);transition:.16s;box-shadow:var(--ig-shadow);}
.ig-btn:hover{transform:translateY(-1px);}
.ig-btn:disabled{opacity:.6;cursor:default;transform:none;}
.ig-btn.sm{font-size:12px;padding:7px 13px;border-radius:10px;}
.ig-btn.primary{background:var(--ig-accent);color:#fff;border-color:transparent;}
.ig-btn.ghost{background:none;border:none;box-shadow:none;color:var(--ig-mut);padding:7px 9px;font-weight:700;}
.ig-btn.ghost:hover{color:var(--ig-ink);}
.ig-btn.danger{background:none;border:none;box-shadow:none;color:var(--ig-down);padding:7px 10px;}
.ig-btn.danger:hover{transform:none;filter:brightness(1.05);}
.ig-input{width:100%;font-family:Nunito,system-ui,sans-serif;font-size:14px;font-weight:600;color:var(--ig-ink);background:var(--ig-panel);border:1px solid var(--ig-line);border-radius:12px;padding:10px 13px;outline:none;transition:.16s;}
.ig-input:focus{border-color:var(--ig-accent);box-shadow:0 0 0 3px rgba(78,154,196,.14);}
.ig-input::placeholder{color:var(--ig-dim);font-weight:600;}
.ig-evt{font-size:11px;font-weight:700;padding:6px 11px;border-radius:10px;border:1px solid var(--ig-line);background:var(--ig-panel2);color:var(--ig-mut);cursor:pointer;display:inline-flex;align-items:center;gap:6px;transition:.14s;white-space:nowrap;}
.ig-evt:hover{border-color:var(--ig-accent);color:var(--ig-ink);}
.ig-evt.on{background:var(--ig-accent);border-color:transparent;color:#fff;}
.ig-spin{animation:ig-spin 1s linear infinite;}
@keyframes ig-spin{to{transform:rotate(360deg);}}
.ig-fade{animation:ig-fade .35s ease both;}
@keyframes ig-fade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
`

let _igStyleInjected = false
function IGStyle() {
  useEffect(() => {
    if (_igStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-ig-style', '1')
    el.textContent = IG_CSS
    document.head.appendChild(el)
    _igStyleInjected = true
  }, [])
  return null
}

// ---- event-type catalog (icon + one-liner for reference chips) -------------
const EVENT_META = {
  'lead.captured':          { icon: UserPlus,      label: 'Lead captured',          desc: 'A new lead entered the funnel.' },
  'campaign.sent':          { icon: Megaphone,     label: 'Campaign sent',          desc: 'A broadcast campaign finished sending.' },
  'order.created':          { icon: ShoppingCart,  label: 'Order created',          desc: 'A new order was placed.' },
  'order.paid':             { icon: CreditCard,    label: 'Order paid',             desc: 'An order was successfully paid.' },
  'conversation.assigned':  { icon: Inbox,         label: 'Conversation assigned',  desc: 'An inbox conversation was assigned to an agent.' },
  'message.received':       { icon: MessageSquare, label: 'Message received',       desc: 'An inbound message arrived.' },
}
const metaFor = (t) => EVENT_META[t] || { icon: Zap, label: t, desc: '' }

// ---- helpers --------------------------------------------------------------
const fmtTime = (ts) => {
  if (!ts) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return String(ts)
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
}
// Never render a raw object as a child — stringify payloads safely.
const fmtPayload = (p) => {
  if (p == null) return ''
  if (typeof p === 'string') return p
  try { return JSON.stringify(p) } catch { return String(p) }
}

export default function Integrations() {
  const session = getSession()
  const [eventTypes, setEventTypes] = useState([])

  useEffect(() => {
    if (!session?.namespaceId) return
    axios.get(`${API}/event-types`)
      .then(r => setEventTypes(Array.isArray(r.data?.event_types) ? r.data.event_types : []))
      .catch(() => setEventTypes(Object.keys(EVENT_META)))
  }, [session?.namespaceId])

  return (
    <div className="ig-root space-y-8">
      <IGStyle />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="ig-title">
            <Plug size={24} style={{ color: 'var(--ig-accent)' }} /> Integrations
          </h2>
          <p className="ig-sub">
            Wire ReRight into your stack. Register outbound webhooks, choose which events they receive, and watch the live event stream.
          </p>
        </div>
        <span className="ig-pill" style={{ background: 'rgba(79,174,90,.15)', color: 'var(--ig-up)' }}>
          <ShieldCheck size={13} /> Signed deliveries · HMAC-SHA256
        </span>
      </div>

      {!session?.namespaceId && (
        <div className="ig-card" style={{ textAlign: 'center', padding: '48px 22px' }}>
          <Plug size={32} style={{ color: 'var(--ig-dim)', margin: '0 auto 12px' }} />
          <p className="ig-muted" style={{ margin: 0 }}>Connect a workspace to manage integrations.</p>
        </div>
      )}

      {session?.namespaceId && (
        <>
          <EventCatalog types={eventTypes} />
          <Webhooks eventTypes={eventTypes} />
          <EventFeed eventTypes={eventTypes} />
        </>
      )}
    </div>
  )
}

// ---- EVENT CATALOG (reference chips) --------------------------------------
function EventCatalog({ types }) {
  const list = types.length ? types : Object.keys(EVENT_META)
  return (
    <div className="ig-card ig-fade" style={{ background: 'linear-gradient(160deg, rgba(78,154,196,.06), transparent 60%)' }}>
      <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
        <span className="ig-sec-h"><Radio size={16} style={{ color: 'var(--ig-accent)' }} /> Event catalog</span>
      </div>
      <p className="ig-sub" style={{ marginBottom: 16 }}>
        The events ReRight can emit. Subscribe any endpoint to one or more — an endpoint with none selected receives them all.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(t => {
          const m = metaFor(t)
          return (
            <div key={t} className="ig-soft" style={{ padding: 13, display: 'flex', gap: 11, alignItems: 'flex-start' }}>
              <span className="ig-icobox" style={{ background: 'rgba(78,154,196,.14)', color: 'var(--ig-accent)' }}><m.icon size={16} /></span>
              <div className="min-w-0">
                <p className="ig-body ig-mono" style={{ fontSize: 12.5, fontWeight: 800 }}>{t}</p>
                {m.desc && <p className="ig-muted" style={{ fontSize: 12, marginTop: 3 }}>{m.desc}</p>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---- WEBHOOKS -------------------------------------------------------------
function Webhooks({ eventTypes }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [busyId, setBusyId] = useState('')
  const [newSecret, setNewSecret] = useState(null) // {id, secret} shown once after create

  const load = () => {
    setLoading(true)
    axios.get(`${API}/webhooks`)
      .then(r => setItems(Array.isArray(r.data?.webhooks) ? r.data.webhooks : []))
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load webhooks'))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const onCreated = (ep) => {
    setShowForm(false)
    if (ep?.secret) setNewSecret({ id: ep.id, secret: ep.secret })
    load()
  }

  const toggleActive = async (ep) => {
    setBusyId(ep.id)
    try {
      await axios.patch(`${API}/webhooks/${ep.id}`, { active: !ep.active })
      toast.success(ep.active ? 'Endpoint paused' : 'Endpoint resumed')
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Update failed') }
    finally { setBusyId('') }
  }

  const testEp = async (ep) => {
    setBusyId(ep.id)
    try {
      const r = await axios.post(`${API}/webhooks/${ep.id}/test`)
      const n = r.data?.delivered_to
      toast.success(typeof n === 'number' ? `Test event queued → ${n} delivery${n === 1 ? '' : 's'}` : 'Test event queued')
      window.dispatchEvent(new CustomEvent('ig-events-refresh'))
    } catch (err) { toast.error(err.response?.data?.error || 'Test failed') }
    finally { setBusyId('') }
  }

  const remove = async (ep) => {
    if (!window.confirm(`Delete this webhook endpoint?\n${ep.url}`)) return
    setBusyId(ep.id)
    try {
      await axios.delete(`${API}/webhooks/${ep.id}`)
      toast.success('Endpoint deleted')
      if (newSecret?.id === ep.id) setNewSecret(null)
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed') }
    finally { setBusyId('') }
  }

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <h3 className="ig-sec-h"><Webhook size={14} /> Webhook endpoints</h3>
        <button onClick={() => setShowForm(s => !s)} className="ig-btn primary sm">
          {showForm ? <X size={13} /> : <Plus size={13} />} {showForm ? 'Cancel' : 'Add endpoint'}
        </button>
      </div>

      {showForm && <WebhookForm eventTypes={eventTypes} onCreated={onCreated} onCancel={() => setShowForm(false)} />}

      {newSecret && (
        <div className="ig-card tight ig-fade" style={{ marginBottom: 12, border: '1px solid var(--ig-warn)', background: 'rgba(234,180,60,.08)' }}>
          <p className="ig-elab" style={{ color: 'var(--ig-warn)', marginBottom: 8 }}><KeyRound size={12} /> Signing secret — shown once</p>
          <p className="ig-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
            Store this now. Verify each delivery's <span className="ig-mono">X-Reright-Signature</span> header against it. It will not be shown again.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="ig-mono ig-soft" style={{ padding: '8px 12px', fontSize: 12.5, wordBreak: 'break-all', flex: 1, minWidth: 220 }}>{newSecret.secret}</code>
            <button
              onClick={() => { try { navigator.clipboard.writeText(newSecret.secret); toast.success('Secret copied') } catch { toast.error('Copy failed') } }}
              className="ig-btn sm"><Copy size={13} /> Copy</button>
            <button onClick={() => setNewSecret(null)} className="ig-btn ghost"><Check size={14} /> Done</button>
          </div>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="ig-card ig-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading endpoints…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="ig-card" style={{ textAlign: 'center', padding: '40px 22px' }}>
          <Webhook size={28} style={{ color: 'var(--ig-dim)', margin: '0 auto 8px' }} />
          <p className="ig-muted" style={{ fontSize: 13.5, margin: 0 }}>No webhook endpoints yet. Add one to start receiving events.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(ep => {
          const subs = Array.isArray(ep.events) ? ep.events : []
          const wildcard = subs.length === 0
          return (
            <div key={ep.id} className="ig-card tight ig-fade" style={{ borderLeft: `4px solid ${ep.active ? 'var(--ig-up)' : 'var(--ig-dim)'}` }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex gap-3 min-w-0">
                  <span className="ig-icobox" style={{ marginTop: 1, background: ep.active ? 'rgba(79,174,90,.15)' : 'rgba(124,135,148,.14)', color: ep.active ? 'var(--ig-up)' : 'var(--ig-mut)' }}>
                    <Link2 size={16} />
                  </span>
                  <div className="min-w-0">
                    <p className="ig-narr ig-mono" style={{ fontSize: 13.5, wordBreak: 'break-all' }}>{ep.url}</p>
                    <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8 }}>
                      <span className="ig-pill" style={{ background: ep.active ? 'rgba(79,174,90,.15)' : 'rgba(124,135,148,.14)', color: ep.active ? 'var(--ig-up)' : 'var(--ig-mut)' }}>
                        {ep.active ? <Power size={11} /> : <PowerOff size={11} />} {ep.active ? 'Active' : 'Paused'}
                      </span>
                      {ep.has_secret && <span className="ig-chip"><KeyRound size={11} /> Secret set</span>}
                      <span className="ig-dim" style={{ fontSize: 11.5 }}>Added {fmtTime(ep.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 10 }}>
                      {wildcard
                        ? <span className="ig-chip" style={{ color: 'var(--ig-accent)', borderColor: 'var(--ig-accent)' }}><Radio size={11} /> All events</span>
                        : subs.map(t => {
                            const m = metaFor(t)
                            return <span key={t} className="ig-chip"><m.icon size={11} /> {t}</span>
                          })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => testEp(ep)} disabled={busyId === ep.id} className="ig-btn sm" title="Send a sample event">
                    <Send size={13} className={busyId === ep.id ? 'ig-spin' : ''} /> Test
                  </button>
                  <button onClick={() => toggleActive(ep)} disabled={busyId === ep.id} className="ig-btn ghost" title={ep.active ? 'Pause' : 'Resume'}>
                    {ep.active ? <PowerOff size={15} /> : <Power size={15} />}
                  </button>
                  <button onClick={() => remove(ep)} disabled={busyId === ep.id} className="ig-btn danger" title="Delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function WebhookForm({ eventTypes, onCreated, onCancel }) {
  const catalog = eventTypes.length ? eventTypes : Object.keys(EVENT_META)
  const [url, setUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [selected, setSelected] = useState([])
  const [saving, setSaving] = useState(false)

  const toggle = (t) => setSelected(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t])

  const submit = async (e) => {
    e.preventDefault()
    if (!/^https?:\/\//i.test(url.trim())) { toast.error('Enter a valid http(s) URL'); return }
    setSaving(true)
    try {
      const body = { url: url.trim(), events: selected }
      if (secret.trim()) body.secret = secret.trim()
      const r = await axios.post(`${API}/webhooks`, body)
      toast.success('Endpoint registered')
      onCreated(r.data || {})
    } catch (err) { toast.error(err.response?.data?.error || 'Could not create endpoint') }
    finally { setSaving(false) }
  }

  return (
    <form onSubmit={submit} className="ig-card ig-fade" style={{ marginBottom: 12 }}>
      <p className="ig-elab" style={{ marginBottom: 8 }}><Plus size={12} /> New webhook endpoint</p>
      <div className="space-y-4">
        <div>
          <label className="ig-dim" style={{ fontSize: 12, fontWeight: 800, display: 'block', marginBottom: 6 }}>Endpoint URL</label>
          <input className="ig-input ig-mono" style={{ fontSize: 13 }} placeholder="https://your-app.com/webhooks/reright"
            value={url} onChange={e => setUrl(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="ig-dim" style={{ fontSize: 12, fontWeight: 800, display: 'block', marginBottom: 8 }}>
            Subscribe to events <span style={{ fontWeight: 600, color: 'var(--ig-dim)' }}>— none selected = receive all</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {catalog.map(t => {
              const m = metaFor(t)
              const on = selected.includes(t)
              return (
                <button type="button" key={t} onClick={() => toggle(t)} className={`ig-evt ${on ? 'on' : ''}`}>
                  {on ? <Check size={12} /> : <m.icon size={12} />} {t}
                </button>
              )
            })}
          </div>
        </div>
        <div>
          <label className="ig-dim" style={{ fontSize: 12, fontWeight: 800, display: 'block', marginBottom: 6 }}>
            Signing secret <span style={{ fontWeight: 600, color: 'var(--ig-dim)' }}>— optional, auto-generated if blank</span>
          </label>
          <input className="ig-input ig-mono" style={{ fontSize: 13 }} placeholder="Leave blank to generate a secure secret"
            value={secret} onChange={e => setSecret(e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-2" style={{ marginTop: 16 }}>
        <button type="submit" disabled={saving} className="ig-btn primary sm">
          {saving ? <RefreshCw size={13} className="ig-spin" /> : <Check size={13} />} {saving ? 'Saving…' : 'Register endpoint'}
        </button>
        <button type="button" onClick={onCancel} className="ig-btn ghost sm">Cancel</button>
      </div>
    </form>
  )
}

// ---- EVENT FEED -----------------------------------------------------------
function EventFeed({ eventTypes }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState('')

  const load = () => {
    setLoading(true)
    const params = { limit: 100 }
    if (filter) params.type = filter
    axios.get(`${API}/events`, { params })
      .then(r => setEvents(Array.isArray(r.data?.events) ? r.data.events : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false))
  }
  useEffect(load, [filter])
  useEffect(() => {
    const h = () => load()
    window.addEventListener('ig-events-refresh', h)
    return () => window.removeEventListener('ig-events-refresh', h)
  }, [filter])

  const catalog = eventTypes.length ? eventTypes : Object.keys(EVENT_META)

  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap" style={{ marginBottom: 12 }}>
        <h3 className="ig-sec-h"><Activity size={14} /> Recent events</h3>
        <button onClick={load} disabled={loading} className="ig-btn sm">
          <RefreshCw size={13} className={loading ? 'ig-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2" style={{ marginBottom: 12 }}>
        <button onClick={() => setFilter('')} className={`ig-evt ${filter === '' ? 'on' : ''}`}>All types</button>
        {catalog.map(t => (
          <button key={t} onClick={() => setFilter(t)} className={`ig-evt ${filter === t ? 'on' : ''}`}>{t}</button>
        ))}
      </div>

      {loading && events.length === 0 && (
        <div className="ig-card ig-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading events…</div>
      )}
      {!loading && events.length === 0 && (
        <div className="ig-card" style={{ textAlign: 'center', padding: '40px 22px' }}>
          <Activity size={28} style={{ color: 'var(--ig-dim)', margin: '0 auto 8px' }} />
          <p className="ig-muted" style={{ fontSize: 13.5, margin: 0 }}>
            No events {filter ? `of type "${filter}" ` : ''}yet. Emitted events appear here — send a test from any endpoint above.
          </p>
        </div>
      )}

      {events.length > 0 && (
        <div className="ig-card tight" style={{ padding: 0, overflow: 'hidden' }}>
          {events.map((ev, i) => {
            const m = metaFor(ev.type)
            const payloadStr = fmtPayload(ev.payload)
            const isTest = ev.payload && typeof ev.payload === 'object' && ev.payload.test
            return (
              <div key={ev.id || i} className="ig-fade" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 18px', borderTop: i === 0 ? 'none' : '1px solid var(--ig-line)' }}>
                <span className="ig-icobox" style={{ width: 30, height: 30, background: 'rgba(78,154,196,.14)', color: 'var(--ig-accent)' }}><m.icon size={15} /></span>
                <div className="min-w-0" style={{ flex: 1 }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="ig-body ig-mono" style={{ fontSize: 12.5, fontWeight: 800 }}>{ev.type}</span>
                    {isTest && <span className="ig-pill" style={{ background: 'rgba(234,180,60,.16)', color: 'var(--ig-warn)' }}>Test</span>}
                    <span className="ig-dim ig-tabnum" style={{ fontSize: 11.5 }}>{fmtTime(ev.created_at)}</span>
                  </div>
                  {payloadStr && (
                    <p className="ig-muted ig-mono" style={{ fontSize: 11.5, marginTop: 5, wordBreak: 'break-word', lineHeight: 1.5 }}>{payloadStr}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
